import {
  Component,
  type CSSProperties,
  type ErrorInfo,
  type ReactNode,
} from 'react'

const FALLBACK_STYLES: Record<string, CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0e0e10',
    color: '#f0eee9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily:
      '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: '#1c1c1f',
    border: '1px solid #2a2a2f',
    borderRadius: '14px',
    padding: '40px 36px',
    maxWidth: '560px',
    width: '100%',
    boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
  },
  eyebrow: {
    fontFamily: '"DM Mono", ui-monospace, "SFMono-Regular", monospace',
    fontSize: '11px',
    letterSpacing: '0.18em',
    textTransform: 'none',
    color: '#d4582d',
    marginBottom: '16px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: 700,
    margin: '0 0 12px',
  },
  paragraph: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#b8b3a8',
    margin: '0 0 16px',
  },
  details: {
    margin: '20px 0 24px',
    background: '#0e0e10',
    border: '1px solid #2a2a2f',
    borderRadius: '10px',
    padding: '14px 16px',
    fontFamily: '"DM Mono", ui-monospace, "SFMono-Regular", monospace',
    fontSize: '12px',
    color: '#88827a',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  primary: {
    appearance: 'none',
    background: '#d4582d',
    color: '#0e0e10',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: 600,
    fontSize: '13px',
    cursor: 'pointer',
  },
  secondary: {
    appearance: 'none',
    background: 'transparent',
    color: '#f0eee9',
    border: '1px solid #2a2a2f',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: 500,
    fontSize: '13px',
    cursor: 'pointer',
  },
}

export interface ErrorBoundaryProps {
  children?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo, errorId: string) => void
}

interface ErrorBoundaryState {
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null, errorInfo: null, errorId: null }
    this.handleReload = this.handleReload.bind(this)
    this.handleReset = this.handleReset.bind(this)
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      error,
      errorId: `err_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
    if (typeof this.props.onError === 'function') {
      try {
        this.props.onError(error, errorInfo, this.state.errorId || '')
      } catch {
        // Reporting failure must never re-trigger the boundary.
      }
    }
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('studio:error-boundary', {
            detail: {
              message: error?.message || 'Unknown error',
              stack: error?.stack || null,
              componentStack: errorInfo?.componentStack || null,
              errorId: this.state.errorId,
            },
          })
        )
      } catch {
        // ignore
      }
    }
  }

  handleReload(): void {
    if (typeof window !== 'undefined') window.location.reload()
  }

  handleReset(): void {
    this.setState({ error: null, errorInfo: null, errorId: null })
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children

    const errorMessage =
      this.state.error?.message || 'An unexpected error occurred.'
    const componentStack = this.state.errorInfo?.componentStack || ''

    return (
      <div style={FALLBACK_STYLES.page} role="alert">
        <div style={FALLBACK_STYLES.card}>
          <div style={FALLBACK_STYLES.eyebrow}>Studio · runtime fault</div>
          <h1 style={FALLBACK_STYLES.heading}>Something broke in the UI.</h1>
          <p style={FALLBACK_STYLES.paragraph}>
            The interface caught an error before it could render. Your data is
            safe — it lives on the server, not in this view. Reload to recover,
            or copy the trace below for a postmortem.
          </p>
          <pre style={FALLBACK_STYLES.details}>
            <strong>{errorMessage}</strong>
            {componentStack ? `\n${componentStack.trim()}` : ''}
            {this.state.errorId ? `\n\nerror_id: ${this.state.errorId}` : ''}
          </pre>
          <div style={FALLBACK_STYLES.buttonRow}>
            <button
              type="button"
              style={FALLBACK_STYLES.primary}
              onClick={this.handleReload}
            >
              Reload Studio
            </button>
            <button
              type="button"
              style={FALLBACK_STYLES.secondary}
              onClick={this.handleReset}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
